import React, { useCallback } from "react";
import { FixedSizeList as List } from "react-window";

import Checkbox from "@cayuse/shared-react.components.form.inputs.checkbox";

import SimpleSearchBar from "components/SimpleSearchBar";

import { MODAL_ID } from "./DownloadCSVModal";
import styles from "./styles.css";

const ROW_HEIGHT = 20;
const MAX_VISIBLE_ROWS = 12;
const LIST_HEIGHT = ROW_HEIGHT * MAX_VISIBLE_ROWS;

const FormModalContent = ({
	dictionary,
	onSearchChange,
	search,
	toggleIsSelectAll,
	isSelectAll,
	hasOptions,
	sortedOptions,
	selections,
	toggleSelection,
	popupDescription,
	updateMessage
}) => {
	const onKeyDown = event => {
		const listItem = event.target.closest("li");
		const currentCheckbox = event.target;

		switch (event.key) {
		case "ArrowDown":
			event.preventDefault();

			const nextListItem = listItem?.nextElementSibling;
			const nextCheckbox = nextListItem?.querySelector("input[type='checkbox']");

			if (nextCheckbox) {
				currentCheckbox.tabIndex = -1;
				nextCheckbox.tabIndex = 0;
				nextCheckbox.focus();
			}
			break;

		case "ArrowUp":
			event.preventDefault();
			const prevListItem = listItem?.previousElementSibling;
			const prevCheckbox = prevListItem?.querySelector("input[type='checkbox']");

			if (prevCheckbox) {
				currentCheckbox.tabIndex = -1;
				prevCheckbox.tabIndex = 0;
				prevCheckbox.focus();
			}
			break;

		default:
			break;
		}
	};

	const row = useCallback(
		({ index, style }) => {
			const option = sortedOptions[index];
			const nextOption = sortedOptions[index + 1];
			const isChecked = selections.has(option.compositeKey);

			return (
				<li key={`label-${option.compositeKey}`} style={style}>
					<Checkbox
						id={`checkbox-${option.compositeKey}`}
						label={option.columnHeader}
						onChange={() => toggleSelection(option, nextOption)}
						onKeyDown={onKeyDown}
						checked={isChecked}
						tabIndex={index === 0 ? 0 : -1}
					/>
				</li>
			);
		},
		[ selections, toggleSelection, sortedOptions ]
	);

	return (
		<>
			<div className={styles.heading}>
				{popupDescription || dictionary.heading || "Select columns for download to CSV. There is no limit to the number of columns that can be selected for download."}
			</div>

			<div className={styles.search}>
				<h3 className="accessibility-text">Search to Filter Available Columns</h3>
				<p id="csvSearchDescription" className="accessibility-text">
					Enter text in the search to narrow the list of available columns to those that
					include the entered search text. For example, entering "project" will show only items
					containing that word.
				</p>
				<p className="accessibility-text" role="alert">Showing {sortedOptions.length} of {sortedOptions.length} search results</p>
				<SimpleSearchBar
					aria-controls={`${MODAL_ID}-itemlist`}
					aria-label="Filter columns"
					aria-describedby="csvSearchDescription"
					onChange={onSearchChange}
					value={search}
				/>
			</div>

			<div className={styles.selectAllWrapper}>
				<Checkbox
					id="checkbox-select-all"
					key={"csv-select-all"}
					label={dictionary.selectAll || "Select All"}
					onClick={toggleIsSelectAll}
					checked={isSelectAll}
				/>
			</div>

			<div className={styles.labelListWrapper}>
				<div className={styles.upperFadeOverlay} />

				<div
					id="columns-list-wrapper"
					className={styles.labelList}
					role="application"
					aria-label="Use arrow keys (Up and Down) to navigate between columns. Press Tab to move to the next element."
				>
					<span role="alert" aria-live="polite" className="accessibility-text">
						{updateMessage}
					</span>
					<List
						height={LIST_HEIGHT}
						itemCount={sortedOptions?.length || 0}
						itemSize={ROW_HEIGHT}
						width="100%"
					>
						{hasOptions && row}
					</List>
				</div>

				<div className={styles.lowerFadeOverlay} />
			</div>
		</>
	);
};


export default FormModalContent;
